import { useState, useEffect } from 'react';
import { Pagination } from '@aosenuma/ui';
import { perfilMarca, type AccionAudit } from '@aosenuma/lib-types';
import { getSupabase, isSupabaseConfigured, useUser } from '@aosenuma/lib-supabase';
import { emitAudit } from '@aosenuma/lib-audit';

interface Evento {
  ts: string;
  accion: AccionAudit | string;
  parcela: string;
  detalle: string;
  /** Email del actor que ejecutó la acción (snapshot en eventos_negociacion). */
  actor_email: string | null;
  /** Rol funcional al momento de la acción (admin/tecnico/cliente/auditor). */
  actor_rol: string | null;
  /** IP y user-agent capturados en el evento (forense LFPDPPP). */
  ip: string | null;
  user_agent: string | null;
  /** Campos opcionales para enriquecer la búsqueda. */
  titular_masked?: string | null;
  num_parcela?: number | null;
  municipio?: string | null;
  /** Payload completo del audit log — útil para debugging / forensia. */
  payload?: Record<string, unknown> | null;
  /** Fecha + hora pre-formateada (es-MX) para que el search global pueda
   *  matchear "09/05/26" o "19:29" sin recomputar en cada filter pass. */
  fechaSearch: string;
}

const ROL_LABEL: Record<string, string> = {
  admin_aosenuma: 'admin',
  tecnico_aosenuma: 'técnico',
  cliente_elawan: 'cliente',
  auditor: 'auditor',
};

/**
 * Filtro por tipo de actor — qué grupo está generando el evento.
 *
 * Un aosenuma admin/técnico, como proveedor del servicio, debe poder
 * monitorear tanto la actividad propia (staff) como la del cliente (Elawan).
 * Por defecto muestra "staff" para que la primera carga sea la operación
 * interna, igual que cuando el filtro era `actor_id = self`. El cliente
 * sale on-demand al elegirlo en el dropdown.
 *
 * Lista hardcoded por ahora porque Elawan es el único cliente piloto. Cuando
 * agreguemos más tenants, esto se hidrata desde `tenants` table o desde una
 * query a roles distintos en `eventos_negociacion`.
 */
// `value` es la llave que viaja a la API — no se toca nunca. Solo la etiqueta
// visible pasa por el perfil de marca.
const TIPO_ACTOR_OPTIONS: { value: string; label: string }[] = [
  { value: 'staff',           label: 'Equipo aosenuma' },
  { value: 'cliente_elawan',  label: perfilMarca().etiquetaRolCliente },
  { value: '',                label: 'Todos (staff + cliente)' },
];

const ACCIONES_DISPONIBLES: { value: string; label: string }[] = [
  { value: '',                                label: 'Todas las acciones' },
  { value: 'gestion.create',                  label: 'Reporte creado' },
  { value: 'gestion.update',                  label: 'Cambio de estado / edición' },
  { value: 'comentario.create',               label: 'Comentario' },
  { value: 'visita.create',                   label: 'Visita registrada' },
  { value: 'expediente.upload',               label: 'Subida de documento' },
  { value: 'expediente.download',             label: 'Descarga / vista de doc' },
  { value: 'expediente.delete',               label: 'Documento dado de baja' },
  { value: 'propietario.unmask',              label: 'Consulta PII' },
  { value: 'propietario.unmask_for_hydration', label: 'Apertura captura (PII)' },
  { value: 'propietarios.bulk_unmask',        label: 'Export PDF nombres' },
  // export.pdf lo emite el dashboard cliente al "Exportar reporte" via
  // RPC log_export_event (mig 0031). Es independiente de bulk_unmask:
  // queda registrado SIEMPRE que se exporta, con o sin PII.
  { value: 'export.pdf',                      label: 'Export reporte (PDF)' },
  { value: 'audit.export',                    label: 'Export bitácora (.jsonl)' },
  { value: 'titular.changed',                 label: 'Cambio de titular' },
  { value: 'irregularidad.flag',              label: 'Irregularidad detectada' },
  { value: 'irregularidad.update',            label: 'Irregularidad editada' },
  { value: 'irregularidad.resolve',           label: 'Irregularidad resuelta' },
  // Auth events (mig 0044) — proyectados desde auth.audit_log_entries vía
  // trigger. Sin estos, BitacoraPage no mostraba inicios de sesión y la
  // cadena LFPDPPP quedaba incompleta (no se podía correlacionar un
  // descifrado PII con la sesión que lo originó).
  { value: 'auth.login',                      label: 'Inicio de sesión' },
  { value: 'auth.logout',                     label: 'Cierre de sesión' },
  { value: 'auth.mfa.verify',                 label: 'MFA verificado' },
  { value: 'auth.mfa.challenge',              label: 'MFA challenge' },
  { value: 'auth.mfa.factor_verified',        label: 'MFA enrolled' },
  { value: 'auth.mfa.factor_revoked',         label: 'MFA revocado' },
];

/** Mapea accion → texto humano. Si la accion no está en el dict, devuelve la
 *  accion cruda (forensia). El payload se anexa para detalle si tiene info útil. */
function accionLabel(accion: string): string {
  const found = ACCIONES_DISPONIBLES.find((a) => a.value === accion);
  return found?.label ?? accion;
}

/** Construye un detalle legible desde el payload. Cada accion tiene su shape. */
function buildDetalle(accion: string, payload: Record<string, unknown> | null): string {
  if (!payload) return accionLabel(accion);
  const p = payload;
  switch (accion) {
    case 'gestion.update': {
      const old = (p.estado_old as string | null) ?? null;
      const nu = (p.estado_new as string | null) ?? null;
      if (nu && old && nu !== old) return `Estado: ${old} → ${nu}`;
      if (nu) return `Estado: ${nu}`;
      return 'Edición de gestión';
    }
    case 'expediente.upload':
    case 'expediente.download':
    case 'expediente.delete': {
      const fn = (p.filename as string | null) ?? (p.r2_key as string | null) ?? '';
      const sz = p.size_bytes ? ` · ${Math.round(Number(p.size_bytes) / 1024)} KB` : '';
      const tipo = (p.tipo_doc as string | null) ?? '';
      return `${tipo}${tipo ? ' · ' : ''}${fn}${sz}`;
    }
    case 'propietario.unmask':
    case 'propietario.unmask_for_hydration': {
      const v = p.pii_version ?? p.version;
      const proposito = p.proposito as string | undefined;
      const parts = [v != null ? `v${v}` : null, proposito].filter(Boolean);
      return parts.length ? `Descifra PII · ${parts.join(' · ')}` : 'Descifra PII';
    }
    case 'propietarios.bulk_unmask':
      return `Export PDF nombres · ${p.descifrados ?? 0}/${p.total ?? 0}`;
    case 'titular.changed':
      return `Titular cambió · ${p.prev_titular ?? '?'} → ${p.new_titular ?? '?'}`;
    case 'comentario.create':
      return (p.cuerpo as string | undefined)?.slice(0, 120) ?? 'Comentario';
    case 'visita.create': {
      const fecha = p.fecha_visita as string | undefined;
      const narr = p.narrativa as string | undefined;
      const interes = p.interes as string | undefined;
      const parts = [
        fecha ? `fecha ${fecha}` : null,
        interes ? `interés ${interes}` : null,
        narr ? narr.slice(0, 80) : null,
      ].filter(Boolean);
      return parts.length ? parts.join(' · ') : 'Visita registrada';
    }
    case 'gestion.create': {
      const estado = p.estado as string | undefined;
      const tipo = p.tipo as string | undefined;
      return [tipo, estado ? `estado ${estado}` : null].filter(Boolean).join(' · ') || 'Reporte creado';
    }
    case 'irregularidad.flag': {
      // Payload moderno (mig 0074+): { subtipo, gravedad }. Legacy: { tipo, descripcion }.
      const subtipo = (p.subtipo as string | undefined) ?? (p.tipo as string | undefined);
      const gravedad = p.gravedad as string | undefined;
      const desc = p.descripcion as string | undefined;
      return [subtipo, gravedad, desc?.slice(0, 80)].filter(Boolean).join(' · ') || 'Irregularidad detectada';
    }
    case 'irregularidad.resolve': {
      // Payload mig 0076: { subtipo, gravedad, resolucion }. Si no hay
      // resolucion, mostramos solo la identidad de la irregularidad.
      const subtipo = p.subtipo as string | undefined;
      const gravedad = p.gravedad as string | undefined;
      const resolucion = p.resolucion as string | undefined;
      const head = [subtipo, gravedad].filter(Boolean).join(' · ') || 'Irregularidad resuelta';
      return resolucion ? `${head} · ${resolucion.slice(0, 100)}` : head;
    }
    case 'irregularidad.update': {
      // Payload mig 0074: { subtipo, old, new } con changes por campo.
      const subtipo = p.subtipo as string | undefined;
      const oldChg = p.old as Record<string, unknown> | undefined;
      const newChg = p.new as Record<string, unknown> | undefined;
      const partes: string[] = [];
      if (subtipo) partes.push(subtipo);
      if (oldChg && newChg) {
        if (oldChg.gravedad !== newChg.gravedad) {
          partes.push(`gravedad ${oldChg.gravedad} → ${newChg.gravedad}`);
        }
        if (oldChg.descripcion !== newChg.descripcion) {
          partes.push('descripción editada');
        }
      }
      return partes.join(' · ') || 'Irregularidad editada';
    }
    case 'auth.login':
    case 'auth.logout':
    case 'auth.mfa.verify':
    case 'auth.mfa.challenge':
    case 'auth.mfa.factor_verified':
    case 'auth.mfa.factor_revoked': {
      const provider = (p.provider as string | undefined)
        ?? (((p.traits as Record<string, unknown> | undefined)?.provider) as string | undefined);
      const ip = (p.ip as string | undefined) ?? null;
      const parts = [provider ? `via ${provider}` : null, ip ? `ip ${ip}` : null].filter(Boolean);
      return parts.length ? `${accionLabel(accion)} · ${parts.join(' · ')}` : accionLabel(accion);
    }
    default:
      return accionLabel(accion);
  }
}

/** Eventos por página. Sube respecto al valor histórico (10) porque ahora la
 *  paginación es server-side y traemos lo justo — no hay penalización por
 *  cargar más por página. 25 cabe bien sin scroll en pantallas comunes. */
const PAGE_SIZE = 25;
/** Cap defensivo para el export JSONL — evita que un click descargue
 *  millones de rows si el operador no aplicó filtros. Si se necesita más,
 *  el usuario re-exporta por chunks usando rango de fechas. */
const EXPORT_MAX = 10000;

/** Escapa los wildcards de PostgREST/SQL (%, _) para que el ILIKE matchee
 *  el carácter literal y no interprete el input como pattern. */
function escapeIlike(s: string): string {
  return s.replace(/[%_]/g, (c) => `\\${c}`);
}

/** Sanitiza una fecha ISO de inicio de día (00:00:00 local → ISO UTC). */
function isoStartOfDay(date: string): string {
  return new Date(`${date}T00:00:00`).toISOString();
}
/** Sanitiza una fecha ISO de fin de día (23:59:59.999 local → ISO UTC). */
function isoEndOfDay(date: string): string {
  const d = new Date(`${date}T23:59:59.999`);
  return d.toISOString();
}

export function BitacoraPage() {
  const { user } = useUser();
  const [eventos, setEventos] = useState<Evento[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [filtroAccion, setFiltroAccion] = useState('');
  const [filtroParcela, setFiltroParcela] = useState('');
  const [filtroDesde, setFiltroDesde] = useState('');
  const [filtroHasta, setFiltroHasta] = useState('');
  const [busqueda, setBusqueda] = useState('');
  // 'staff' default = comportamiento histórico (operación interna).
  // 'cliente_elawan' = activity del cliente. '' = ambos.
  const [filtroActor, setFiltroActor] = useState<string>('staff');
  const [pagina, setPagina] = useState(1);

  // Carga el audit log desde `v_bitacora_enriched` (mig 0054). Diferencias
  // vs. la versión anterior:
  //   - Filtros + paginación SERVER-SIDE (no más cap de 500, navegamos
  //     histórico de cualquier tamaño).
  //   - `actor_rol_efectivo` viene de la vista (COALESCE payload.rol →
  //     allowed_emails.rol_app), así eventos viejos sin `payload.rol` ya
  //     no caen al cubo "staff por inferencia"; se clasifican correctamente
  //     en su rol real.
  //   - `count: 'exact'` para el paginador.
  //   - Re-fetch al cambiar cualquier filtro (con debounce 300ms en los
  //     inputs de texto para no spamear la DB mientras el usuario escribe).
  // RLS: la vista hereda RLS de `eventos_negociacion` con security_invoker,
  // así que un cliente nunca ve la página y aosenuma_staff/auditor ven
  // todo el tenant.
  useEffect(() => {
    if (!user?.id || !isSupabaseConfigured()) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    // Debounce solo cuando cambia un input de texto. Selects y dates
    // recargan inmediato porque el usuario "termina" el input al click.
    const debounceMs = busqueda || filtroParcela ? 300 : 0;
    const handle = setTimeout(() => {
      void (async () => {
        const sb = getSupabase();
        let q = sb
          .from('v_bitacora_enriched')
          .select(
            'id, accion, actor_email, actor_rol_efectivo, payload, ip, user_agent, created_at, parcela_pid, num_parcela, municipio',
            { count: 'exact' },
          );

        // Filtro de actor → server-side. NULL actor_rol_efectivo se
        // clasifica como "indefinido" (eventos sin email — triggers de
        // sistema). Solo aparece en "Todos".
        if (filtroActor === 'cliente_elawan') {
          q = q.eq('actor_rol_efectivo', 'cliente_elawan');
        } else if (filtroActor === 'staff') {
          q = q.in('actor_rol_efectivo', ['admin_aosenuma', 'tecnico_aosenuma', 'auditor']);
        }

        if (filtroAccion) q = q.eq('accion', filtroAccion);
        if (filtroDesde) q = q.gte('created_at', isoStartOfDay(filtroDesde));
        if (filtroHasta) q = q.lte('created_at', isoEndOfDay(filtroHasta));

        // Filtro de parcela: ID público (ILIKE) o número (eq exact si es
        // dígitos puros — match preciso para num_parcela).
        if (filtroParcela) {
          const term = filtroParcela.trim();
          const escaped = escapeIlike(term);
          if (/^\d+$/.test(term)) {
            q = q.or(`parcela_pid.ilike.%${escaped}%,num_parcela.eq.${term}`);
          } else {
            q = q.ilike('parcela_pid', `%${escaped}%`);
          }
        }

        // Búsqueda libre global → multi-column ILIKE con OR. Cubre los
        // campos de la vista (actor_email, parcela_pid, municipio, accion,
        // actor_rol_efectivo). Búsquedas complejas dentro del payload
        // pueden agregarse luego con un `searchable_text` indexado GIN.
        if (busqueda.trim()) {
          const escaped = escapeIlike(busqueda.trim());
          q = q.or(
            `actor_email.ilike.%${escaped}%,` +
              `parcela_pid.ilike.%${escaped}%,` +
              `municipio.ilike.%${escaped}%,` +
              `accion.ilike.%${escaped}%,` +
              `actor_rol_efectivo.ilike.%${escaped}%`,
          );
        }

        const from = (pagina - 1) * PAGE_SIZE;
        const to = from + PAGE_SIZE - 1;
        q = q.order('created_at', { ascending: false }).range(from, to);

        const { data, count, error: qErr } = await q;
        if (cancelled) return;
        if (qErr) {
          setError(qErr.message);
          setLoading(false);
          return;
        }
        type Row = {
          id: string; accion: string;
          actor_email: string | null;
          actor_rol_efectivo: string | null;
          payload: Record<string, unknown> | null;
          ip: string | null;
          user_agent: string | null;
          created_at: string;
          parcela_pid: string | null;
          num_parcela: number | null;
          municipio: string | null;
        };
        const rows = (data as unknown as Row[] | null) ?? [];
        const mapped: Evento[] = rows.map((r) => {
          const d = new Date(r.created_at);
          const fechaCorta = d.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: '2-digit' });
          const horaCorta = d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
          return {
            ts: r.created_at,
            accion: r.accion,
            parcela: r.parcela_pid ?? '',
            num_parcela: r.num_parcela,
            municipio: r.municipio,
            detalle: buildDetalle(r.accion, r.payload),
            actor_email: r.actor_email,
            actor_rol: r.actor_rol_efectivo,
            ip: r.ip,
            user_agent: r.user_agent,
            payload: r.payload,
            fechaSearch: `${fechaCorta} ${horaCorta}`,
          };
        });
        setEventos(mapped);
        setTotal(count ?? 0);
        setLoading(false);
      })();
    }, debounceMs);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [user?.id, filtroActor, filtroAccion, filtroDesde, filtroHasta, filtroParcela, busqueda, pagina]);

  // Paginación server-side: `total` viene del count: 'exact' de Supabase,
  // y `eventos` es la página actual ya recortada por .range().
  const totalPags = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const eventosVisibles = eventos;

  function resetPagina() { setPagina(1); }

  function limpiarFiltros() {
    setFiltroAccion('');
    setFiltroParcela('');
    setFiltroDesde('');
    setFiltroHasta('');
    setBusqueda('');
    setFiltroActor('staff');
    setPagina(1);
  }

  /**
   * Exporta TODOS los eventos que matchean los filtros actuales (no solo
   * la página visible) como NDJSON / .jsonl. Hace una query separada sin
   * paginación, capada a EXPORT_MAX para evitar accidentes.
   *
   * Tres fricciones intencionales:
   *  1. Confirm dialog: lista cuántos rows + qué filtros aplican, así el
   *     operador no descarga "todo el tenant" por accidente.
   *  2. Cap EXPORT_MAX: si el match excede el cap, advierte y refusa
   *     (forzar a refinar filtros — un dump de millones de rows desde
   *     el browser no es buena idea).
   *  3. Audit propio (`audit.export`): emite un evento al BFF antes de
   *     descargar. Chain of custody LFPDPPP queda cerrada incluso si el
   *     navegador bloquea el download.
   *
   * Formato: NDJSON (un evento por línea, JSON completo). Compatible con
   * jq / Splunk / ELK sin transformar.
   */
  async function handleExportJsonl() {
    if (total === 0) {
      window.alert('No hay eventos para exportar con los filtros actuales.');
      return;
    }
    if (total > EXPORT_MAX) {
      window.alert(
        `Demasiados eventos (${total}) — el cap de export es ${EXPORT_MAX}. ` +
          `Por favor refina con filtros (rango de fechas, acción, actor) antes de exportar.`,
      );
      return;
    }
    const filtroDesc = [
      filtroActor !== 'staff' && `actor=${filtroActor || 'todos'}`,
      filtroAccion && `accion=${filtroAccion}`,
      filtroParcela && `parcela=${filtroParcela}`,
      filtroDesde && `desde=${filtroDesde}`,
      filtroHasta && `hasta=${filtroHasta}`,
      busqueda && `q="${busqueda}"`,
    ].filter(Boolean).join(' · ') || 'sin filtros — equipo aosenuma (default)';

    const ok = window.confirm(
      `¿Exportar ${total} evento${total === 1 ? '' : 's'} a un archivo .jsonl?\n\n` +
        `Filtros aplicados: ${filtroDesc}\n\n` +
        `El archivo incluye payload completo de cada evento. Esta descarga ` +
        `quedará registrada en la bitácora como "audit.export" para ` +
        `trazabilidad (LFPDPPP).`,
    );
    if (!ok) return;

    // Re-query SIN paginación para traer todos los matches (no solo la página).
    const sb = getSupabase();
    let q = sb
      .from('v_bitacora_enriched')
      .select(
        'id, accion, actor_email, actor_rol_efectivo, payload, ip, user_agent, created_at, parcela_pid, num_parcela, municipio',
      );
    if (filtroActor === 'cliente_elawan') {
      q = q.eq('actor_rol_efectivo', 'cliente_elawan');
    } else if (filtroActor === 'staff') {
      q = q.in('actor_rol_efectivo', ['admin_aosenuma', 'tecnico_aosenuma', 'auditor']);
    }
    if (filtroAccion) q = q.eq('accion', filtroAccion);
    if (filtroDesde) q = q.gte('created_at', isoStartOfDay(filtroDesde));
    if (filtroHasta) q = q.lte('created_at', isoEndOfDay(filtroHasta));
    if (filtroParcela) {
      const term = filtroParcela.trim();
      const escaped = escapeIlike(term);
      if (/^\d+$/.test(term)) {
        q = q.or(`parcela_pid.ilike.%${escaped}%,num_parcela.eq.${term}`);
      } else {
        q = q.ilike('parcela_pid', `%${escaped}%`);
      }
    }
    if (busqueda.trim()) {
      const escaped = escapeIlike(busqueda.trim());
      q = q.or(
        `actor_email.ilike.%${escaped}%,` +
          `parcela_pid.ilike.%${escaped}%,` +
          `municipio.ilike.%${escaped}%,` +
          `accion.ilike.%${escaped}%,` +
          `actor_rol_efectivo.ilike.%${escaped}%`,
      );
    }
    q = q.order('created_at', { ascending: false }).limit(EXPORT_MAX);

    const { data, error: qErr } = await q;
    if (qErr) {
      window.alert(`Error al exportar: ${qErr.message}`);
      return;
    }
    type Row = {
      id: string; accion: string;
      actor_email: string | null;
      actor_rol_efectivo: string | null;
      payload: Record<string, unknown> | null;
      ip: string | null;
      user_agent: string | null;
      created_at: string;
      parcela_pid: string | null;
      num_parcela: number | null;
      municipio: string | null;
    };
    const rows = (data as unknown as Row[] | null) ?? [];

    const lines = rows.map((r) => JSON.stringify({
      ts: r.created_at,
      accion: r.accion,
      accion_label: accionLabel(r.accion),
      actor_email: r.actor_email,
      actor_rol: r.actor_rol_efectivo,
      parcela: r.parcela_pid,
      num_parcela: r.num_parcela,
      municipio: r.municipio,
      detalle: buildDetalle(r.accion, r.payload),
      ip: r.ip,
      user_agent: r.user_agent,
      payload: r.payload,
    }));
    const blob = new Blob([lines.join('\n') + '\n'], { type: 'application/x-ndjson' });

    // Audit del export ANTES de descargar — si el browser bloquea el
    // download, el registro ya quedó.
    void emitAudit('audit.export', {
      meta: {
        total: rows.length,
        filtros: {
          actor: filtroActor || null,
          accion: filtroAccion || null,
          parcela: filtroParcela || null,
          desde: filtroDesde || null,
          hasta: filtroHasta || null,
          busqueda: busqueda || null,
        },
        formato: 'jsonl',
        proposito: 'incidente.share',
      },
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    a.download = `bitacora-${filtroActor || 'todos'}-${stamp}.jsonl`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  // No contamos filtroActor='staff' como "filtro activo" porque es el default.
  const hayFiltros =
    filtroAccion ||
    filtroParcela ||
    filtroDesde ||
    filtroHasta ||
    busqueda ||
    filtroActor !== 'staff';

  return (
    <section className="panel">
      <header className="panel-header">
        <h1>Bitácora</h1>
        <p className="panel-sub">
          Audit log del tenant · append-only · inalterable. Por defecto muestra
          la actividad del equipo aosenuma; usa el selector para ver la del
          cliente.
        </p>
      </header>

      <div className="panel-body">

        {/* ── Barra de filtros ──────────────────────────────────── */}
        <div className="bitacora-filtros glass-panel">
          <div className="bitacora-filtros-row">

            {/* Búsqueda libre */}
            <div className="bfiltro-field bfiltro-search">
              <svg viewBox="0 0 24 24" width={13} height={13} fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="bfiltro-icon">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                className="bfiltro-input"
                placeholder="Buscar global: detalle · ID · número · titular · municipio · correo · IP · rol · acción · fecha (09/05)…"
                value={busqueda}
                onChange={(e) => { setBusqueda(e.target.value); resetPagina(); }}
              />
            </div>

            {/* Selector de actor: equipo aosenuma vs cliente Elawan.
                Default 'staff' replica el comportamiento histórico
                (operación interna). Al elegir un cliente, el listado
                muestra la actividad de ese tenant para auditoría. */}
            <select
              className="bfiltro-select"
              value={filtroActor}
              onChange={(e) => { setFiltroActor(e.target.value); resetPagina(); }}
              title="Quién genera el evento"
            >
              {TIPO_ACTOR_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>

            {/* Tipo de acción */}
            <select
              className="bfiltro-select"
              value={filtroAccion}
              onChange={(e) => { setFiltroAccion(e.target.value); resetPagina(); }}
            >
              {ACCIONES_DISPONIBLES.map((a) => (
                <option key={a.value} value={a.value}>{a.label}</option>
              ))}
            </select>

            {/* Parcela */}
            <input
              className="bfiltro-input bfiltro-parcela"
              placeholder="ID, número o titular…"
              value={filtroParcela}
              onChange={(e) => { setFiltroParcela(e.target.value.toUpperCase()); resetPagina(); }}
            />

            {/* Rango de fechas */}
            <input
              className="bfiltro-input bfiltro-date"
              type="date"
              value={filtroDesde}
              onChange={(e) => { setFiltroDesde(e.target.value); resetPagina(); }}
              title="Desde"
            />
            <span className="bfiltro-sep">—</span>
            <input
              className="bfiltro-input bfiltro-date"
              type="date"
              value={filtroHasta}
              onChange={(e) => { setFiltroHasta(e.target.value); resetPagina(); }}
              title="Hasta"
            />

            {/* Limpiar */}
            {hayFiltros && (
              <button className="bfiltro-clear" onClick={limpiarFiltros} title="Limpiar filtros">
                ✕ Limpiar
              </button>
            )}

            {/* Exportar JSONL — exporta TODOS los matches (no solo página
                visible) en una query separada, capada a EXPORT_MAX. Pide
                confirmación y registra audit.export. */}
            <button
              type="button"
              className="bfiltro-clear"
              onClick={() => { void handleExportJsonl(); }}
              title="Descargar todos los eventos que matchean los filtros como .jsonl. Útil para compartir en un incidente."
              disabled={loading || total === 0}
              style={{ marginLeft: 'auto' }}
            >
              ⤓ Exportar .jsonl
            </button>
          </div>

          <div className="bfiltro-meta">
            {loading
              ? 'Cargando eventos…'
              : error
                ? `Error: ${error}`
                : total === 0
                  ? 'Sin eventos que coincidan con los filtros'
                  : `${total} evento${total === 1 ? '' : 's'} en total · página ${pagina}/${totalPags}`}
          </div>
        </div>

        {/* ── Tabla ─────────────────────────────────────────────── */}
        <div className="glass-panel" style={{ padding: 0, overflow: 'hidden', marginTop: 12 }}>
          <div
            className="bitacora-row"
            style={{
              background: 'rgba(255,255,255,0.03)',
              borderBottom: '1px solid var(--glass-border)',
              fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em',
              color: 'var(--text-dim)', fontWeight: 500, fontFamily: 'var(--font-sans)',
            }}
          >
            <span>Fecha</span><span>Actor</span><span>Acción</span><span>Parcela</span><span>Detalle</span>
          </div>

          {loading ? (
            <div className="empty">Cargando eventos…</div>
          ) : error ? (
            <div className="empty" style={{ color: 'var(--st-rojo, #b00)' }}>
              Error al cargar la bitácora: {error}
            </div>
          ) : total === 0 ? (
            <div className="empty">No hay eventos que coincidan con los filtros.</div>
          ) : (
            eventosVisibles.map((e, i) => {
              // Tooltip forense: ISO completo + IP + user-agent (LFPDPPP).
              // Hover sobre la celda Fecha o Actor revela la trazabilidad cruda.
              const tsIso = new Date(e.ts).toISOString();
              const forenseTip = [
                `ts: ${tsIso}`,
                e.ip ? `ip: ${e.ip}` : null,
                e.user_agent ? `ua: ${e.user_agent.slice(0, 120)}` : null,
              ].filter(Boolean).join(' · ');
              const rolLbl = e.actor_rol ? (ROL_LABEL[e.actor_rol] ?? e.actor_rol) : null;
              return (
                <div className="bitacora-row" key={i}>
                  <span
                    style={{ color: 'var(--text-dim)', display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}
                    title={forenseTip}
                  >
                    <span>{new Date(e.ts).toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: '2-digit' })}</span>
                    <span style={{ fontSize: 11, opacity: 0.8 }}>
                      {new Date(e.ts).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}
                    </span>
                  </span>
                  <span
                    style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25, minWidth: 0 }}
                    title={forenseTip}
                  >
                    <span style={{
                      fontSize: 11, color: 'var(--text-mid)',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {e.actor_email ?? 'sistema'}
                    </span>
                    {rolLbl && (
                      <span style={{
                        fontSize: 10, color: 'var(--text-dim)', textTransform: 'uppercase',
                        letterSpacing: '0.04em', fontFamily: 'var(--font-display)',
                      }}>
                        {rolLbl}
                      </span>
                    )}
                  </span>
                  <span><span className="accion" title={e.accion}>{accionLabel(e.accion)}</span></span>
                  <span style={{ fontFamily: 'var(--font-display)', fontSize: 11 }}>{e.parcela || '—'}</span>
                  <span style={{ color: 'var(--text-mid)' }}>{e.detalle}</span>
                </div>
              );
            })
          )}

          <Pagination current={pagina} total={totalPags} onChange={setPagina} />
        </div>

      </div>
    </section>
  );
}
